import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {
  CheckCircle2,
  Shield,
  Calendar,
  Ticket,
  FileText,
  ArrowRight,
  Download,
  Home,
  RefreshCw,
  Sparkles,
  Clock,
  Check,
  Phone,
  Mail,
  Star,
  Award,
  Users,
  CreditCard,
  MapPin,
  AlertCircle,
  ChevronRight,
} from "lucide-react";
import { toast } from "react-toastify";
import paymentService from "../../api/PaymentService";
import Footer from "../../components/landing/Footer";
import { useAppSelector } from "../../store/hooks";
import GuestNavbar from "../../components/navigations/GuestNavbar";
import AuthNavbar from "../../components/navigations/AuthNavbar";

// ─────────────────────────────────────────────────────────────────
// Per-type visual & content configuration
// ─────────────────────────────────────────────────────────────────
const TYPE_CONFIG = {
  insurance: {
    gradient: "from-emerald-600 to-green-600",
    lightBg: "from-emerald-50 to-green-50",
    border: "border-emerald-200",
    ring: "ring-emerald-300",
    badge: "bg-emerald-100 text-emerald-800 border-emerald-300",
    iconBg: "bg-emerald-500",
    iconColor: "text-emerald-600",
    tagLine: "Your insurance policy is now active and providing coverage.",
    primaryCta: { label: "View My Policies", path: "/my-policies" },
    secondaryCta: { label: "Browse More Insurance", path: "/insurance" },
    whatsNext: [
      "Check your email — policy documents and receipt have been sent.",
      "Your policy is now active and provides immediate coverage.",
      "File a claim anytime from the My Policies section.",
      "Download your policy certificate for your records.",
    ],
  },
  event: {
    gradient: "from-emerald-600 to-teal-600",
    lightBg: "from-teal-50 to-emerald-50",
    border: "border-teal-200",
    ring: "ring-teal-300",
    badge: "bg-teal-100 text-teal-800 border-teal-300",
    iconBg: "bg-teal-500",
    iconColor: "text-teal-600",
    tagLine: "Your event tickets have been booked and confirmed.",
    primaryCta: { label: "My Event Bookings", path: "/my-event-registrations" },
    secondaryCta: { label: "Explore Events", path: "/events" },
    whatsNext: [
      "Check your email for booking confirmation and e-tickets.",
      "Download your receipt to use as your entry pass.",
      "Mark your calendar so you don't miss the event.",
      "View all your bookings under My Event Bookings.",
    ],
  },
  conference: {
    gradient: "from-orange-500 to-orange-600",
    lightBg: "from-orange-50 to-amber-50",
    border: "border-orange-200",
    ring: "ring-orange-300",
    badge: "bg-orange-100 text-orange-800 border-orange-300",
    iconBg: "bg-orange-500",
    iconColor: "text-orange-600",
    tagLine: "Your conference registration is confirmed.",
    primaryCta: { label: "My Registrations", path: "/conference-registrations" },
    secondaryCta: { label: "Browse Conferences", path: "/conference-trade-mission" },
    whatsNext: [
      "Check your email for your official registration confirmation.",
      "Keep your application ID handy for on-site check-in.",
      "A digital receipt has been sent to your registered email.",
      "Contact our support team if you need any changes.",
    ],
  },
  quote: {
    gradient: "from-emerald-600 to-green-600",
    lightBg: "from-emerald-50 to-green-50",
    border: "border-emerald-200",
    ring: "ring-emerald-300",
    badge: "bg-emerald-100 text-emerald-800 border-emerald-300",
    iconBg: "bg-emerald-500",
    iconColor: "text-emerald-600",
    tagLine: "Your quoted policy has been activated.",
    primaryCta: { label: "View My Policies", path: "/my-policies" },
    secondaryCta: { label: "My Quote Requests", path: "/my-quote-requests" },
    whatsNext: [
      "Your quoted policy is now active.",
      "Policy documents have been sent to your email.",
      "Contact your agent for any adjustments.",
      "File a claim anytime from My Policies.",
    ],
  },
};

const INSURANCE_LABELS = {
  daily_commuter: "Daily Commuter",
  intercity_travel: "Intercity Travel",
  okada_rider: "Okada Rider",
  taxi_and_bolt: "Taxi & Bolt Driver",
  life_insurance: "Life Insurance",
  general_insurance: "General Insurance",
  travel_insurance: "Travel Insurance",
  tripsecure_insurance: "TripSecure Insurance",
  health_insurance: "Health Insurance",
  home_insurance: "Home Insurance",
  accident_insurance: "Accident Insurance",
  fire_insurance: "Fire Insurance",
  marine_insurance: "Marine Insurance",
  liability_insurance: "Liability Insurance",
};

const MAX_CYCLES = 5;
const POLL_MS = 8000;

// ─────────────────────────────────────────────────────────────────
// Small animated confetti particles
// ─────────────────────────────────────────────────────────────────
const PARTICLE_COLORS = ["#10b981", "#34d399", "#f59e0b", "#fbbf24", "#6366f1", "#8b5cf6", "#ec4899"];

function Particles() {
  const items = Array.from({ length: 18 }, (_, i) => ({
    id: i,
    x: 20 + Math.random() * 60,
    y: 10 + Math.random() * 80,
    color: PARTICLE_COLORS[i % PARTICLE_COLORS.length],
    delay: i * 0.08,
    size: 6 + Math.random() * 8,
  }));

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {items.map((p) => (
        <motion.div
          key={p.id}
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
            background: p.color,
            borderRadius: Math.random() > 0.5 ? "50%" : "20%",
          }}
          initial={{ scale: 0, opacity: 1, y: 0, rotate: 0 }}
          animate={{
            scale: [0, 1.2, 0],
            opacity: [1, 1, 0],
            y: [0, -(40 + Math.random() * 60)],
            x: [(Math.random() - 0.5) * 60],
            rotate: [0, 360 * (Math.random() > 0.5 ? 1 : -1)],
          }}
          transition={{ delay: p.delay, duration: 1.8, ease: "easeOut" }}
          className="absolute"
        />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Loading / Verifying UI
// ─────────────────────────────────────────────────────────────────
function VerifyingState({ cycleCount }) {
  const tips = [
    "Contacting payment provider…",
    "Confirming transaction details…",
    "Activating your account…",
    "Almost there, hang tight…",
    "Finalising your record…",
  ];
  const tip = tips[Math.min(cycleCount, tips.length - 1)];

  return (
    <motion.div
      key="verifying"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="flex flex-col items-center justify-center py-24 text-center"
    >
      <div className="relative mb-8">
        <div className="w-24 h-24 rounded-full border-4 border-emerald-200 animate-spin border-t-emerald-500" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center">
            <Shield size={24} className="text-emerald-500" />
          </div>
        </div>
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Verifying Payment</h2>
      <p className="text-gray-500 mb-4 max-w-xs">{tip}</p>
      {cycleCount > 0 && (
        <div className="flex gap-1 mt-2">
          {Array.from({ length: MAX_CYCLES }, (_, i) => (
            <div
              key={i}
              className={`w-2 h-2 rounded-full transition-colors ${
                i < cycleCount ? "bg-emerald-500" : "bg-gray-200"
              }`}
            />
          ))}
        </div>
      )}
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Pending / polling state
// ─────────────────────────────────────────────────────────────────
function PendingState({ onManualVerify, isVerifying, reference }) {
  return (
    <motion.div
      key="pending"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="flex flex-col items-center py-16 text-center"
    >
      <motion.div
        animate={{ scale: [1, 1.1, 1] }}
        transition={{ repeat: Infinity, duration: 2 }}
        className="w-24 h-24 bg-amber-100 rounded-full flex items-center justify-center mb-6 ring-4 ring-amber-200"
      >
        <Clock size={40} className="text-amber-500" />
      </motion.div>

      <h2 className="text-2xl font-bold text-gray-900 mb-3">Payment Processing</h2>
      <p className="text-gray-600 mb-2 max-w-sm">
        Your payment has been submitted and is being processed. This usually takes just a moment.
      </p>
      {reference && (
        <p className="text-xs font-mono text-gray-400 mb-8 bg-gray-100 px-3 py-1.5 rounded-full">
          Ref: {reference}
        </p>
      )}

      <div className="p-5 bg-amber-50 border border-amber-200 rounded-2xl max-w-md mb-8 text-left space-y-3">
        {[
          "Check that your Mobile Money PIN was entered correctly.",
          "Ensure your wallet has sufficient funds.",
          "If the amount was debited, tap Confirm below to notify us.",
        ].map((tip, i) => (
          <div key={i} className="flex gap-3 items-start">
            <span className="flex-shrink-0 w-5 h-5 bg-amber-400 text-white rounded-full flex items-center justify-center text-xs font-bold">
              {i + 1}
            </span>
            <p className="text-sm text-amber-900">{tip}</p>
          </div>
        ))}
      </div>

      <button
        onClick={onManualVerify}
        disabled={isVerifying}
        className="flex items-center gap-2 px-8 py-3.5 text-sm font-bold text-white bg-amber-500 hover:bg-amber-600 rounded-xl shadow-lg transition-all disabled:opacity-50"
      >
        {isVerifying ? (
          <>
            <div className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
            Confirming…
          </>
        ) : (
          <>
            <Check size={18} />
            I Completed the Payment
          </>
        )}
      </button>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main Summary Card — adapts to payment type
// ─────────────────────────────────────────────────────────────────
function SummaryCard({ ctx, cfg, transactionId }) {
  const type = ctx?.type || "insurance";

  if (type === "event") {
    return (
      <div className={`p-6 bg-gradient-to-br ${cfg.lightBg} rounded-2xl border ${cfg.border} shadow-sm space-y-4`}>
        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <Ticket size={20} className={cfg.iconColor} />
          Booking Summary
        </h3>
        <div className="space-y-3 text-sm">
          {ctx?.eventTitle && (
            <Row label="Event" value={ctx.eventTitle} bold />
          )}
          {ctx?.ticketType && (
            <Row label="Ticket Type" value={ctx.ticketType} />
          )}
          {ctx?.quantity && (
            <Row label="Quantity" value={`${ctx.quantity} ticket${ctx.quantity > 1 ? "s" : ""}`} />
          )}
          {(ctx?.amount || ctx?.totalAmount) && (
            <Row label="Amount Paid" value={`GH₵ ${Number(ctx.totalAmount || ctx.amount || 0).toFixed(2)}`} bold accent />
          )}
          {(ctx?.bookingId || transactionId) && (
            <Row label="Booking ID" value={ctx.bookingId || transactionId} mono />
          )}
        </div>
      </div>
    );
  }

  if (type === "conference") {
    return (
      <div className={`p-6 bg-gradient-to-br ${cfg.lightBg} rounded-2xl border ${cfg.border} shadow-sm space-y-4`}>
        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <Calendar size={20} className={cfg.iconColor} />
          Registration Summary
        </h3>
        <div className="space-y-3 text-sm">
          {ctx?.eventTitle && (
            <Row label="Event" value={ctx.eventTitle} bold />
          )}
          {ctx?.selectedTicket && (
            <Row label="Ticket" value={typeof ctx.selectedTicket === "object" ? ctx.selectedTicket.name : ctx.selectedTicket} />
          )}
          {(ctx?.amount !== undefined || ctx?.selectedTicket?.price !== undefined) && (
            <Row
              label="Amount Paid"
              value={Number(ctx?.selectedTicket?.price ?? ctx?.amount ?? 0) === 0 ? "FREE" : `GH₵ ${Number(ctx.selectedTicket?.price || ctx.amount || 0).toFixed(2)}`}
              bold
              accent
            />
          )}
          {(ctx?.applicationId || transactionId) && (
            <Row label="Application ID" value={ctx.applicationId || transactionId} mono />
          )}
          {ctx?.registrationId && (
            <Row label="Registration ID" value={ctx.registrationId} mono />
          )}
        </div>
      </div>
    );
  }

  // insurance / quote
  return (
    <div className={`p-6 bg-gradient-to-br ${cfg.lightBg} rounded-2xl border ${cfg.border} shadow-sm space-y-4`}>
      <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
        <Shield size={20} className={cfg.iconColor} />
        Policy Summary
      </h3>
      <div className="space-y-3 text-sm">
        {ctx?.policyType && (
          <Row label="Policy Type" value={INSURANCE_LABELS[ctx.policyType] || ctx.policyType} bold />
        )}
        {ctx?.subtype && ctx.subtype !== ctx.policyType && (
          <Row label="Insurance Type" value={INSURANCE_LABELS[ctx.subtype] || ctx.subtype} />
        )}
        {ctx?.providerName && (
          <Row label="Provider" value={ctx.providerName} />
        )}
        {ctx?.coverLabel && (
          <Row label="Cover" value={ctx.coverLabel} />
        )}
        {ctx?.duration && (
          <Row label="Duration" value={ctx.duration} />
        )}
        {ctx?.amount != null && (
          <Row label="Amount Paid" value={`₵ ${Number(ctx.amount || 0).toFixed(2)}`} bold accent />
        )}
        {transactionId && (
          <Row label="Transaction ID" value={transactionId} mono />
        )}
        {ctx?.reference && (
          <Row label="Reference" value={ctx.reference} mono />
        )}
      </div>
    </div>
  );
}

function Row({ label, value, bold, mono, accent }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-gray-500">{label}</span>
      <span
        className={`${bold ? "font-bold" : "font-medium"} ${
          accent ? "text-emerald-700 text-base" : "text-gray-900"
        } ${mono ? "font-mono text-xs" : ""} text-right max-w-[55%] break-all`}
      >
        {value}
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Success Title / Hero
// ─────────────────────────────────────────────────────────────────
const SUCCESS_TITLES = {
  insurance: { headline: "Policy Activated!", sub: "You're covered — welcome to TripSecure." },
  event: { headline: "Booking Confirmed!", sub: "Your tickets are ready." },
  conference: { headline: "Registration Confirmed!", sub: "You're all set for the event." },
  quote: { headline: "Quote Payment Done!", sub: "Your policy is now active." },
};

// ─────────────────────────────────────────────────────────────────
// Main Page Component
// ─────────────────────────────────────────────────────────────────
export default function PaymentSuccess() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isVerified, user } = useAppSelector((s) => s.auth);

  const [verState, setVerState] = useState("loading"); // loading | verified | pending | timeout
  const [payCtx, setPayCtx] = useState(null);
  const [txId, setTxId] = useState(null);
  const [cycle, setCycle] = useState(0);
  const [isManualVerifying, setIsManualVerifying] = useState(false);
  const [showParticles, setShowParticles] = useState(false);
  const pollingRef = useRef(null);

  // ── Parse context ──────────────────────────────────────────────
  useEffect(() => {
    const reference = searchParams.get("reference") || searchParams.get("trxref") || searchParams.get("ref");
    const typeParam = searchParams.get("type") || "insurance";

    let stored = null;
    try {
      const raw = sessionStorage.getItem("tripsecure_payment_context");
      if (raw) stored = JSON.parse(raw);
    } catch (_) {}

    const locState = location.state || {};

    const ctx = {
      reference: reference || stored?.reference || locState.reference,
      type: stored?.type || typeParam || locState.type || "insurance",
      subtype: stored?.subtype || searchParams.get("policy_type") || locState.policyType || locState.subtype,
      policyType: stored?.policyType || searchParams.get("policy_type") || locState.policyType,
      providerName: stored?.providerName || locState.providerName,
      coverLabel: stored?.coverLabel || locState.coverLabel,
      amount: stored?.amount || searchParams.get("amount") || locState.amount,
      duration: stored?.duration || locState.duration,
      transactionId: stored?.transactionId || searchParams.get("transaction_id") || locState.transactionId,
      eventTitle: stored?.eventTitle || locState.eventTitle,
      slug: stored?.slug || searchParams.get("slug") || locState.slug,
      applicationId: stored?.applicationId || locState.applicationId,
      registrationId: stored?.registrationId || locState.registrationId,
      bookingId: stored?.bookingId || locState.bookingId,
      ticketType: stored?.ticketType || locState.ticketType,
      quantity: stored?.quantity || locState.quantity,
      totalAmount: stored?.totalAmount || locState.totalAmount,
      selectedTicket: stored?.selectedTicket || locState.selectedTicket,
      returnPath: stored?.returnPath || locState.returnPath,
    };

    setPayCtx(ctx);
    setTxId(ctx.transactionId);

    const alreadyVerified = locState.paymentVerified === true;
    const hasRef = ctx.reference || ctx.registrationId;

    if (alreadyVerified || !hasRef) {
      setVerState("verified");
      setShowParticles(true);
    } else {
      setVerState("loading");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Verify payment ─────────────────────────────────────────────
  const runVerification = useCallback(async () => {
    if (!payCtx) return;
    const id = payCtx.reference || payCtx.registrationId;
    if (!id) {
      setVerState("verified");
      setShowParticles(true);
      return;
    }

    if (cycle >= MAX_CYCLES) {
      setVerState("pending");
      return;
    }

    try {
      const res = await paymentService.verifyPayment(id);
      const verified =
        res.status &&
        (res.data?.payment_verified ||
          res.data?.status === "success" ||
          res.data?.status_code === 200);

      if (verified) {
        setTxId(res.data?.transaction_id || res.data?.id || id);
        setVerState("verified");
        setShowParticles(true);
        sessionStorage.removeItem("tripsecure_payment_context");
      } else {
        setCycle((c) => c + 1);
      }
    } catch (_) {
      if (cycle >= MAX_CYCLES - 1) {
        setVerState("pending");
      } else {
        setCycle((c) => c + 1);
      }
    }
  }, [payCtx, cycle]);

  // Initial trigger
  useEffect(() => {
    if (verState === "loading" && payCtx) {
      runVerification();
    }
  }, [verState, payCtx]); // eslint-disable-line react-hooks/exhaustive-deps

  // Polling
  useEffect(() => {
    if (verState === "loading" && cycle > 0) {
      pollingRef.current = setTimeout(runVerification, POLL_MS);
      return () => clearTimeout(pollingRef.current);
    }
  }, [cycle, verState, runVerification]);

  const handleManualVerify = async () => {
    if (!payCtx?.registrationId && !payCtx?.reference) {
      toast.error("No registration ID found for manual verification.");
      return;
    }
    setIsManualVerifying(true);
    try {
      const id = payCtx.registrationId || payCtx.reference;
      const res = await paymentService.verifyPayment(id);
      if (res.status) {
        toast.success("Manual verification submitted. We'll confirm shortly.");
        setVerState("verified");
        setShowParticles(true);
      } else {
        toast.error(res.message || "Verification failed. Please contact support.");
      }
    } catch (_) {
      toast.error("Verification failed. Please contact support.");
    } finally {
      setIsManualVerifying(false);
    }
  };

  // ── Derive config ──────────────────────────────────────────────
  const type = payCtx?.type || "insurance";
  const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.insurance;
  const titles = SUCCESS_TITLES[type] || SUCCESS_TITLES.insurance;
  const showAuthNavbar = isAuthenticated && isVerified;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-50">
      {/* Navbar */}
      {showAuthNavbar ? (
        <AuthNavbar user={user} />
      ) : (
        <GuestNavbar />
      )}

      <div className="px-4 pt-24 pb-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <AnimatePresence mode="wait">
            {/* ── LOADING STATE ── */}
            {verState === "loading" && (
              <VerifyingState key="verifying" cycleCount={cycle} />
            )}

            {/* ── PENDING STATE ── */}
            {verState === "pending" && (
              <PendingState
                key="pending"
                reference={payCtx?.reference}
                onManualVerify={handleManualVerify}
                isVerifying={isManualVerifying}
              />
            )}

            {/* ── VERIFIED / SUCCESS STATE ── */}
            {verState === "verified" && (
              <motion.div
                key="success"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-8"
              >
                {/* ─── Hero Section ─── */}
                <div className="relative text-center py-10 overflow-hidden">
                  {showParticles && <Particles />}

                  {/* Animated success icon */}
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 160, damping: 12 }}
                    className="relative inline-flex items-center justify-center mb-6"
                  >
                    <div className={`relative flex items-center justify-center w-32 h-32 bg-gradient-to-br ${cfg.gradient} rounded-full shadow-2xl`}>
                      <CheckCircle2 size={60} className="text-white drop-shadow-lg" />
                    </div>
                    {/* Ripple rings */}
                    {[1, 2, 3].map((i) => (
                      <motion.div
                        key={i}
                        className={`absolute w-32 h-32 rounded-full border-2 border-emerald-400`}
                        initial={{ scale: 1, opacity: 0.7 }}
                        animate={{ scale: 1 + i * 0.5, opacity: 0 }}
                        transition={{ delay: i * 0.25, duration: 1.4, repeat: Infinity, ease: "easeOut" }}
                      />
                    ))}
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 }}
                  >
                    <h1 className="text-4xl sm:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-gray-600 mb-3">
                      {titles.headline}
                    </h1>
                    <p className="text-lg text-gray-500 mb-5 max-w-md mx-auto">{titles.sub}</p>
                  </motion.div>

                  {/* Reference / ID badge */}
                  {(txId || payCtx?.bookingId || payCtx?.applicationId || payCtx?.reference) && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.4 }}
                      className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full border ${cfg.badge} shadow-md`}
                    >
                      <Award size={18} />
                      <span className="text-sm font-semibold">
                        {type === "event" ? "Booking ID: " : type === "conference" ? "Application ID: " : "Transaction ID: "}
                      </span>
                      <span className="font-mono font-bold text-sm">
                        {payCtx?.bookingId || payCtx?.applicationId || txId || payCtx?.reference}
                      </span>
                    </motion.div>
                  )}
                </div>

                {/* ─── Two column layout ─── */}
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
                  {/* Main content — 3 cols */}
                  <div className="space-y-6 lg:col-span-3">
                    {/* Summary card */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.45 }}
                    >
                      <SummaryCard ctx={payCtx} cfg={cfg} transactionId={txId} />
                    </motion.div>

                    {/* CTA Buttons */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.55 }}
                      className="grid grid-cols-1 gap-3 sm:grid-cols-2"
                    >
                      <motion.button
                        onClick={() => navigate(cfg.primaryCta.path)}
                        whileHover={{ scale: 1.03, y: -2 }}
                        whileTap={{ scale: 0.97 }}
                        className={`flex items-center justify-center gap-2 px-5 py-4 text-sm font-bold text-white bg-gradient-to-r ${cfg.gradient} rounded-xl shadow-xl hover:shadow-2xl transition-all`}
                      >
                        <FileText size={18} />
                        {cfg.primaryCta.label}
                        <ArrowRight size={16} />
                      </motion.button>

                      <motion.button
                        onClick={() => navigate(cfg.secondaryCta.path)}
                        whileHover={{ scale: 1.03, y: -2 }}
                        whileTap={{ scale: 0.97 }}
                        className={`flex items-center justify-center gap-2 px-5 py-4 text-sm font-bold bg-white border-2 ${cfg.border} ${cfg.iconColor} rounded-xl shadow-md hover:shadow-lg transition-all`}
                      >
                        {cfg.secondaryCta.label}
                      </motion.button>
                    </motion.div>

                    {/* What's Next */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.65 }}
                      className={`p-6 bg-gradient-to-br ${cfg.lightBg} rounded-2xl border ${cfg.border}`}
                    >
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <Sparkles size={20} className={cfg.iconColor} />
                        What Happens Next
                      </h3>
                      <div className="space-y-3">
                        {cfg.whatsNext.map((tip, i) => (
                          <motion.div
                            key={i}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.7 + i * 0.1 }}
                            className="flex items-start gap-3"
                          >
                            <div className={`flex-shrink-0 w-6 h-6 ${cfg.iconBg} rounded-full flex items-center justify-center`}>
                              <span className="text-xs font-bold text-white">{i + 1}</span>
                            </div>
                            <p className="text-sm text-gray-700">{tip}</p>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  </div>

                  {/* Sidebar — 2 cols */}
                  <div className="space-y-5 lg:col-span-2">
                    {/* Confirmation checklist */}
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 }}
                      className="sticky top-28 space-y-5"
                    >
                      <div className={`p-6 bg-gradient-to-br ${cfg.lightBg} rounded-2xl border ${cfg.border} shadow-lg`}>
                        <div className="flex items-center gap-3 mb-5 pb-5 border-b border-gray-200">
                          <div className={`w-11 h-11 ${cfg.iconBg} rounded-xl flex items-center justify-center shadow-md`}>
                            <Check size={22} className="text-white" />
                          </div>
                          <div>
                            <p className="font-bold text-gray-900">All Confirmed</p>
                            <p className="text-xs text-gray-500">Everything is in order</p>
                          </div>
                        </div>

                        <div className="space-y-3">
                          {[
                            {
                              icon: CreditCard,
                              label: "Payment Received",
                              desc: "Successfully processed",
                              color: "bg-green-100 text-green-600",
                            },
                            {
                              icon: Mail,
                              label: "Email Sent",
                              desc: "Check your inbox",
                              color: "bg-blue-100 text-blue-600",
                            },
                            ...(type === "insurance" || type === "quote"
                              ? [
                                  {
                                    icon: Shield,
                                    label: "Policy Active",
                                    desc: "Coverage starts now",
                                    color: "bg-emerald-100 text-emerald-600",
                                  },
                                ]
                              : type === "event"
                              ? [
                                  {
                                    icon: Ticket,
                                    label: "Tickets Issued",
                                    desc: "Ready for the event",
                                    color: "bg-teal-100 text-teal-600",
                                  },
                                ]
                              : [
                                  {
                                    icon: Users,
                                    label: "Spot Secured",
                                    desc: "You're on the list",
                                    color: "bg-orange-100 text-orange-600",
                                  },
                                ]),
                          ].map(({ icon: Icon, label, desc, color }, i) => (
                            <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-100">
                              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}>
                                <Icon size={16} />
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-gray-900">{label}</p>
                                <p className="text-xs text-gray-500">{desc}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Support card */}
                      <div className="p-5 bg-white rounded-2xl border border-gray-200 shadow-sm">
                        <p className="text-sm font-bold text-gray-900 mb-1">Need Assistance?</p>
                        <p className="text-xs text-gray-500 mb-4">
                          Our support team is here to help with any questions.
                        </p>
                        <div className="space-y-2">
                          <a
                            href="tel:+233302000000"
                            className="flex items-center gap-2 text-sm text-gray-700 hover:text-emerald-600 transition-colors"
                          >
                            <Phone size={14} className="text-gray-400" />
                            +233 30 200 0000
                          </a>
                          <a
                            href="mailto:support@tripsecuregh.com"
                            className="flex items-center gap-2 text-sm text-gray-700 hover:text-emerald-600 transition-colors"
                          >
                            <Mail size={14} className="text-gray-400" />
                            support@tripsecuregh.com
                          </a>
                        </div>
                        <button
                          onClick={() => navigate("/contact-us")}
                          className="w-full mt-4 px-4 py-2.5 text-sm font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                        >
                          Contact Support
                        </button>
                      </div>

                      {/* Home nav */}
                      <button
                        onClick={() => navigate("/")}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-gray-500 hover:text-gray-700 bg-white border border-gray-200 rounded-xl transition-colors"
                      >
                        <Home size={16} />
                        Back to Home
                      </button>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <Footer />
    </div>
  );
}
